import React from 'react';
import { motion } from 'framer-motion';
import { 
  FiInfo, FiTrendingUp, FiTrendingDown, FiCpu, FiCalendar
} from 'react-icons/fi';
import { 
  RiPlantLine, RiRecycleLine, 
  RiMoneyDollarCircleLine, RiLineChartLine 
} from 'react-icons/ri';

import { MetricCardProps } from '../../types/components';
import Tooltip from './Tooltip';

/**
 * MetricCard component for displaying key metrics in a card format
 */
const MetricCard: React.FC<MetricCardProps> = ({ 
  title, 
  value, 
  description = null, 
  trend = null, 
  trendDirection = null, 
  color = "blue",
  tooltipContent = null
}) => {
  /**
   * Get CSS class for card gradient based on color
   */
  const getColorClass = (color: string): string => {
    switch (color) {
      case 'green': return 'from-emerald-500 to-green-700';
      case 'red': return 'from-rose-500 to-red-700';
      case 'yellow': return 'from-amber-400 to-yellow-600';
      case 'blue':
      default: return 'from-blue-500 to-indigo-700';
    }
  };
  
  /**
   * Get CSS class for background color based on color
   */
  const getBgColorClass = (color: string): string => {
    switch (color) {
      case 'green': return 'bg-green-50';
      case 'red': return 'bg-red-50';
      case 'yellow': return 'bg-yellow-50';
      case 'blue':
      default: return 'bg-blue-50';
    }
  };
  
  /**
   * Get CSS class for icon color based on color
   */
  const getIconColorClass = (color: string): string => {
    switch (color) {
      case 'green': return 'text-green-600';
      case 'red': return 'text-red-600';
      case 'yellow': return 'text-yellow-600';
      case 'blue':
      default: return 'text-blue-600';
    }
  };
  
  /**
   * Get CSS class for border color based on color
   */
  const getBorderColorClass = (color: string): string => {
    switch (color) {
      case 'green': return 'border-green-200';
      case 'red': return 'border-red-200';
      case 'yellow': return 'border-yellow-200';
      case 'blue':
      default: return 'border-blue-200';
    }
  };
  
  /**
   * Get icon component based on metric title
   */
  const getIcon = (title: string) => {
    if (title.includes('Optimal')) return <FiCpu className="text-xl" />;
    if (title.includes('Savings')) return <FiTrendingUp className="text-xl" />;
    if (title.includes('Payback')) return <FiCalendar className="text-xl" />;
    if (title.includes('Carbon')) return <RiPlantLine className="text-xl" />;
    if (title.includes('Emission')) return <RiRecycleLine className="text-xl" />;
    if (title.includes('Rate')) return <RiLineChartLine className="text-xl" />;
    if (title.includes('Cost')) return <RiMoneyDollarCircleLine className="text-xl" />;
    return <FiInfo className="text-xl" />;
  };
  
  /**
   * Generate tooltip content based on title if not provided
   */
  const getDefaultTooltipContent = () => {
    if (title.includes('Optimal Solution')) {
      return (
        <div>
          <p>Solar + Battery is identified as the optimal solution based on:</p>
          <ul className="list-disc list-inside mt-1">
            <li>Lowest levelized cost of energy (LCOE)</li>
            <li>Best return on investment over 25 years</li>
            <li>Lowest carbon emissions profile</li>
            <li>Highest self-sufficiency ratio</li>
          </ul>
        </div>
      );
    }
    
    if (title.includes('Savings')) {
      return (
        <div>
          <p>25-Year Savings represents:</p>
          <ul className="list-disc list-inside mt-1">
            <li>Net present value of all cost savings</li>
            <li>Accounts for time value of money (discounted cash flow)</li>
            <li>Includes energy cost savings and residual value</li>
            <li>Compared to grid-only power consumption</li>
          </ul>
        </div>
      );
    }
    
    if (title.includes('Payback')) {
      return (
        <div>
          <p>Payback Period indicates:</p>
          <ul className="list-disc list-inside mt-1">
            <li>Time needed to recover the initial investment</li>
            <li>Calculated using simple payback method</li>
            <li>Based on annual cash flows without discounting</li>
            <li>Good payback periods are typically under 10 years</li>
          </ul>
        </div>
      );
    }
    
    if (title.includes('LCOE')) {
      return (
        <div>
          <p>Levelized Cost of Energy (LCOE) represents:</p>
          <ul className="list-disc list-inside mt-1">
            <li>Average cost per kWh over system lifetime</li>
            <li>Includes all capital and operational expenses</li>
            <li>Accounts for time value of money (discounted)</li>
            <li>Lower LCOE indicates better economic performance</li>
          </ul>
        </div>
      );
    }
    
    return "Click to learn more about this metric";
  };
  
  const effectiveTooltipContent = tooltipContent || getDefaultTooltipContent();
  
  return (
    <motion.div 
      className={`modern-card bg-white overflow-hidden`}
      whileHover={{ y: -8, boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)" }}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ 
        type: "spring", 
        stiffness: 300, 
        damping: 20,
        duration: 0.3 
      }}
    >
      <div className={`h-1 bg-gradient-to-r ${getColorClass(color)}`}></div>
      <div className="p-6">
        <div className="flex justify-between items-start mb-4">
          <div className="flex items-center">
            <motion.div 
              className={`w-10 h-10 rounded-full ${getBgColorClass(color)} flex items-center justify-center mr-3`}
              whileHover={{ rotate: 10, scale: 1.1 }}
              transition={{ type: "spring", stiffness: 300 }}
            >
              <span className={getIconColorClass(color)}>
                {getIcon(title)}
              </span>
            </motion.div>
            <Tooltip 
              title={"About " + title}
              content={effectiveTooltipContent}
            >
              <div className="flex items-center cursor-help">
                <span className="text-sm font-medium text-gray-700 tooltip-indicator">{title}</span>
                <FiInfo className="ml-1 text-blue-500 h-3.5 w-3.5" />
              </div>
            </Tooltip>
          </div>
          
          {trend && (
            <motion.div 
              className={`flex items-center text-sm font-medium rounded-full px-3 py-1 ${
                trendDirection === 'up' 
                  ? 'bg-green-100 text-green-800' 
                  : 'bg-red-100 text-red-800'
              }`}
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", stiffness: 300 }}
              whileHover={{ scale: 1.05 }}
            >
              <Tooltip
                title={"Trend Analysis"}
                content={
                  <div>
                    <p className="mb-2">
                      {trendDirection === 'up' 
                        ? 'This metric shows a positive trend of ' 
                        : 'This metric shows a negative trend of '
                      }
                      <span className="font-semibold">{trend}</span> compared to the baseline scenario.
                    </p>
                    <div className={`p-2 rounded ${trendDirection === 'up' ? 'bg-green-50' : 'bg-red-50'}`}>
                      <div className="flex items-center">
                        <div className={`h-2 w-2 rounded-full ${trendDirection === 'up' ? 'bg-green-500' : 'bg-red-500'} mr-2`}></div>
                        <span className="text-xs">
                          {trendDirection === 'up' 
                            ? 'Higher values indicate better performance' 
                            : 'Lower values indicate caution areas'
                          }
                        </span>
                      </div>
                    </div>
                  </div>
                }
              >
                <span className="flex items-center px-3 py-1 rounded-full">
                  {trendDirection === 'up' 
                    ? <FiTrendingUp className={`mr-1 text-green-500`} /> 
                    : <FiTrendingDown className={`mr-1 text-red-500`} />
                  }
                  <span className="tooltip-indicator">{trend}</span>
                </span>
              </Tooltip>
            </motion.div>
          )}
        </div>
        
        <div className="mt-2">
          <motion.div 
            className="text-3xl font-bold tracking-tight"
            initial={{ opacity: 0, x: -5 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.1, duration: 0.3 }}
          >
            {value}
          </motion.div>
          {description && (
            <motion.div 
              className="mt-2 text-sm text-gray-500 flex items-center"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2, duration: 0.3 }}
            >
              <span>{description}</span>
              <motion.button
                className={`ml-2 p-1 rounded-full ${getBgColorClass(color)} ${getBorderColorClass(color)} border`}
                whileHover={{ scale: 1.2 }}
                whileTap={{ scale: 0.9 }}
              >
                <FiInfo className={`h-3 w-3 ${getIconColorClass(color)}`} />
              </motion.button>
            </motion.div>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default MetricCard;